import React, { useState, useEffect } from "react";
import "../css/MyProfile.css";
import GmailIcon from "../Images/Gmail.png";
import ChatHistoryIcon from "../Images/ConnectorIcon.png";
import GoogleDriveIcon from "../Images/Google_Drive.png";
import InfoIcon from "../Images/infoIcon.png";
import DesktopFolderIcon from "../Images/DesktopFolder.png";
import DesktopAgentInstallModal from "./DesktopAgentInstallModal_LinuxOnly";
import Footer from "./atoms/footer";

import { purgeAllModelData } from "./hybrid_model_selection";
import Qwen3Manager from "./Qwen3Manager";
import MobileQwen3Manager from "./MobileQwen3Manager";


interface MyProfileProps {
  isDark?: boolean;
}

export function MyProfile({ isDark }: MyProfileProps) {
  // OAuth Configuration - using existing login endpoint
  const CONNECTION_MAPPING = {
    gmail: 'google-oauth2',
    gdrive: 'google-oauth2',
    outlook: 'windowslive',
    onedrive: 'windowslive'
  };

  // Auth identity now lives in the HttpOnly Flask session cookie.
  // JavaScript must not read auth0_sub directly; backend routes derive it
  // from the signed server-side session via credentials: "include".

  // 2) Local states for each toggle
  //    By default, let's say they're false until we load them from DB
  const [primaryEmail, setPrimaryEmail] = useState<string | null>(null);
  const [gmailToggled, setGmailToggled] = useState(false);
  const [gDriveToggled, setGDriveToggled] = useState(false);
  const [outlookToggled, setOutlookToggled] = useState(false);
  const [oneDriveToggled, setOneDriveToggled] = useState(false);
  const [allToggled, setAllToggled] = useState(false); // This is purely local convenience
  const [convHistoryToggled, setConvHistoryToggled] = useState(false);
  const [chatMemoryToggled, setChatMemoryToggled] = useState(false);
  const [desktopToggled, setDesktopToggled] = useState(false);
  // For Account Deletion: add to your existing state
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  // For Desktop Sync (Linux-only message)
  const [showDesktopModal, setShowDesktopModal] = useState(false);
  // ⭐ NEW: State for two-state button
  const [showActivateButton, setShowActivateButton] = useState(false);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [desktopSyncState, setDesktopSyncState] = useState<'idle' | 'confirm_install' | 'activate' | 'complete'>('idle');

  // 3) We'll track the *original states* so we can see which toggles switched from ON -> OFF
  const [originalStates, setOriginalStates] = useState({
    gmail: false,
    gdrive: false,
    outlook: false,
    onedrive: false,
    convHistory: false,
    chatMemory: false,
    desktop: false,
    // "All" is not in DB, so we won't track it
  });

  // 4) On mount, fetch user settings AND primary email from the DB
  useEffect(() => {
    async function loadUserSettings() {
      try {
        const resp = await fetch(`/user-settings`, {
          credentials: "include",
        });
        if (!resp.ok) {
          console.error("Failed to load user settings", resp.statusText);
          return;
        }
        const data = await resp.json();
        // DB returns 0 or 1
        setGmailToggled(data.IntegrationGmail === 1);
        setGDriveToggled(data.IntegrationGoogleDrive === 1);
        setOutlookToggled(data.IntegrationOutlook === 1);
        setOneDriveToggled(data.IntegrationOneDrive === 1);
        setConvHistoryToggled(data.ConversationHistory === 1);
        setChatMemoryToggled(data.ChatEmbeddings === 1);
        setDesktopToggled(data.IntegrationDesktop === 1);

        // store these as "original"
        setOriginalStates({
          gmail: data.IntegrationGmail === 1,
          gdrive: data.IntegrationGoogleDrive === 1,
          outlook: data.IntegrationOutlook === 1,
          onedrive: data.IntegrationOneDrive === 1,
          convHistory: data.ConversationHistory === 1,
          chatMemory: data.ChatEmbeddings === 1,
          desktop: data.IntegrationDesktop === 1,
        });

        // "allToggled" is not from DB; it's purely for your front-end convenience
        // We'll decide how to handle "All" if you want it to sync with everything else.
      } catch (err) {
        console.error("Error loading user settings", err);
      }
    }

    async function loadPrimaryEmail() { // <-- ADDED FUNCTION
      try {
        const resp = await fetch(`/get-primary-email`, {
          credentials: "include",
        });
        if (!resp.ok) {
          console.error("Failed to load user email", resp.statusText);
          return;
        }
        const data = await resp.json();
        if (data.primaryEmail) {
          setPrimaryEmail(data.primaryEmail);
        }
      } catch (err) {
        console.error("Error loading user email", err);
      }
    }

    loadUserSettings();
    loadPrimaryEmail();

  }, []);

  // Handle OAuth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    const oauthError = urlParams.get('oauth_error');
    
    if (oauthSuccess === 'true') {
      // OAuth was successful, restore pending states
      const pendingStates = localStorage.getItem('pendingIntegrationStates');
      const pendingIntegrations = localStorage.getItem('pendingOAuthIntegrations');
      
      if (pendingStates) {
        const states = JSON.parse(pendingStates);
        
        // Restore toggle states
        setGmailToggled(states.gmail);
        setGDriveToggled(states.gdrive);
        setOutlookToggled(states.outlook);
        setOneDriveToggled(states.onedrive);
        setDesktopToggled(states.desktop);
        setConvHistoryToggled(states.convHistory);
        setChatMemoryToggled(states.chatMemory);
        
        // Update original states to reflect the new integration
        setOriginalStates(prev => ({
          ...prev,
          gmail: states.gmail,
          gdrive: states.gdrive,
          outlook: states.outlook,
          onedrive: states.onedrive,
          desktop: states.desktop,
        }));
        
        // Clean up localStorage
        localStorage.removeItem('pendingIntegrationStates');
        localStorage.removeItem('pendingOAuthIntegrations');

        // Show success message
        alert('OAuth integration successful! Your settings have been updated.');
        
        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (oauthError) {
      // OAuth failed, clean up and show error
      localStorage.removeItem('pendingIntegrationStates');
      localStorage.removeItem('pendingOAuthIntegrations');
      
      alert(`OAuth integration failed: ${decodeURIComponent(oauthError)}`);
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // 5) Deletion & Clearing Data
  const deleteSpecificEmbeddings = async (source: string) => {
    const body = { source };
    try {
      const response = await fetch("/delete-specific-embeddings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        console.error("Error:", response.statusText);
      } else {
        console.log("Success deleting:", source);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const deleteAll = async () => {
    const body = { dataType: "all", source: "all" };
    try {
      const response = await fetch("/delete-specific-embeddings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        console.error("Error:", response.statusText);
      } else {
        console.log("Success deleting all");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const clearConversationHistory = async () => {
    try {
      const response = await fetch("/clear-conversation-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        console.error(
          "Error clearing conversation history:",
          response.statusText
        );
      } else {
        console.log("Conversation history cleared");
      }
    } catch (error) {
      console.error("Error clearing conversation history:", error);
    }
  };

  // Detect newly enabled OAuth integrations
  const detectNewOAuthIntegrations = () => {
    const newIntegrations = [];
    
    if (!originalStates.gmail && gmailToggled) {
      newIntegrations.push('gmail');
    }
    if (!originalStates.gdrive && gDriveToggled) {
      newIntegrations.push('gdrive');
    }
    if (!originalStates.outlook && outlookToggled) {
      newIntegrations.push('outlook');
    }
    if (!originalStates.onedrive && oneDriveToggled) {
      newIntegrations.push('onedrive');
    }
    // ❌ REMOVED: Desktop is NOT an OAuth integration - handled separately in handleSave
    
    return newIntegrations;
  };

  // Simplified OAuth flow initiation using existing login endpoint
  const initiateOAuthFlow = (integrations: string[]) => {
    if (integrations.length === 0) {
      console.log("No new integrations to authenticate");
      return;
    }
    
    // Join multiple integrations with comma
    const integrationParam = integrations.join(',');
    
    // Determine connection based on first integration (they should all be same provider anyway)
    const firstIntegration = integrations[0];
    const connection = CONNECTION_MAPPING[firstIntegration as keyof typeof CONNECTION_MAPPING];
    
    console.log(`Starting OAuth for integrations: ${integrationParam}`);
    
    // Store current states for restoration after OAuth
    const currentStates = {
      gmail: gmailToggled,
      gdrive: gDriveToggled,
      outlook: outlookToggled,
      onedrive: oneDriveToggled,
      desktop: desktopToggled,
      convHistory: convHistoryToggled,
      chatMemory: chatMemoryToggled
    };
    localStorage.setItem('pendingIntegrationStates', JSON.stringify(currentStates));
    localStorage.setItem('pendingOAuthIntegrations', JSON.stringify(integrations));
    
    // Redirect to existing login endpoint with profile parameters
    window.location.href = `/login?source=profile&integration=${integrationParam}&connection=${connection}`;
  };

  // 6) Account Deletion: function for deleting a user's account
  const handleDeleteAccount = async () => {
    if (!showDeleteConfirmation) {
      setShowDeleteConfirmation(true);
      return;
    }

    setIsDeleting(true);
  
    try {
      Qwen3Manager.getInstance().reset();         
      MobileQwen3Manager.getInstance().reset();     
      await purgeAllModelData();   

      const response = await fetch('/delete-account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
      });

      if (response.ok) {
        const data = await response.json();
        // Account deleted successfully
        alert('Account deleted successfully. You will be redirected to the Start Page.');
      
        // Clear local storage and cookies
        localStorage.clear();
      
        // Clear session storage
        sessionStorage.clear();

        // Redirect to Auth0 logout URL to fully terminate session
        if (data.logout_url) {
          window.location.href = data.logout_url;
        } else {
        // Redirect to homepage or login page
        window.location.href = '/';
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to delete account: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('An error occurred while deleting your account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Add this function to cancel delete confirmation
  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // 7) Toggle handlers: now we only flip local state – no immediate calls to delete
  const toggleGmail = () => setGmailToggled(!gmailToggled);
  const toggleGDrive = () => setGDriveToggled(!gDriveToggled);
  const toggleDesktop = () => setDesktopToggled(!desktopToggled); 

  // OneDrive and Outlook sync with each other - Microsoft consent is ALL-OR-NOTHING
  const toggleOneDrive = () => {
    const newValue = !oneDriveToggled;
    setOneDriveToggled(newValue);
    setOutlookToggled(newValue);  // Both will have same value
  };
  const toggleOutlook = () => {
    const newValue = !outlookToggled;
    setOutlookToggled(newValue);
    setOneDriveToggled(newValue);  // Both will have same value
  };

  // Chat memory consolidated under one toggle
  const toggleConvHistory = () => setConvHistoryToggled(!convHistoryToggled);
  const toggleChatMemory = () => {
    setChatMemoryToggled(!chatMemoryToggled);
    setConvHistoryToggled(!convHistoryToggled);
  };

  // "All" – for convenience. Let's say toggling "All" just sets everything ON/OFF in local state:
  const toggleAll = () => {
    const newVal = !allToggled;
    setAllToggled(newVal);
    // Example logic: if newVal is false => turn everything off, else on
    setGmailToggled(newVal);
    setGDriveToggled(newVal);
    setOutlookToggled(newVal);
    setOneDriveToggled(newVal);
    setConvHistoryToggled(newVal);
    setChatMemoryToggled(newVal);
    setDesktopToggled(newVal);
  };

  // 8) "Save" button – UPDATED to handle OAuth flows for new integrations
  const handleSave = async () => {
    // ⭐ Check if desktop integration is newly enabled
    const desktopNewlyEnabled = !originalStates.desktop && desktopToggled;
    // Detect newly enabled OAuth integrations
    const newOAuthIntegrations = detectNewOAuthIntegrations();

    // ⭐ If desktop is newly enabled, update Auth0 metadata FIRST and BLOCK OAuth redirect
    if (desktopNewlyEnabled) {
      console.log('[MyProfile] Desktop integration newly enabled, updating Auth0 metadata...');
      
      try {
        const response = await fetch('/consent/desktop-sync/accept', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            source: 'profile'
          })
        });
        
        if (!response.ok) {
          console.error('[MyProfile] Failed to update desktop sync consent');
          alert('Failed to update desktop sync settings. Please try again.');
          return;
        }
        
        const data = await response.json();
        console.log('[MyProfile] Backend response:', data);
        
        // Backend now returns setup_token and download_url directly
        if (data.success && data.setup_token && data.download_url) {
          console.log('[MyProfile] Triggering installer download...');
          
          // Trigger download
          const link = document.createElement('a');
          link.href = data.download_url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log('[MyProfile] Download triggered');
          
          // Store token and setup for 3-state flow
          setSetupToken(data.setup_token);
          setDesktopSyncState('confirm_install'); // NEW STATE

          // ⭐ STORE pending OAuth integrations for AFTER Desktop Sync
          if (newOAuthIntegrations.length > 0) {
            localStorage.setItem('pendingOAuthAfterDesktop', JSON.stringify(newOAuthIntegrations));
          }

          // ⭐ ADD THIS: Show Linux modal if needed
          const userAgent = navigator.userAgent.toLowerCase();
          if (userAgent.includes('linux') && !userAgent.includes('android')) {
            setShowDesktopModal(true);
          }

        } else {
          console.error('[MyProfile] Invalid response from backend:', data);
          alert('Failed to start desktop sync setup. Please try again.');
        }

        // ⭐ DON'T redirect to OAuth yet - let user complete Desktop flow first
        return;
        
      } catch (error) {
        console.error('[MyProfile] Error updating metadata:', error);
        alert('An error occurred while updating desktop sync settings.');
        return;
      }
    }
    
    // Only proceed with OAuth if no desktop setup needed
    if (newOAuthIntegrations.length > 0) {
      initiateOAuthFlow(newOAuthIntegrations);
      return;
    }
  
    // Continue with existing save logic for non-OAuth changes
    // Convert toggles to 1/0 for DB
    const body = {
      IntegrationGmail: gmailToggled ? 1 : 0,
      IntegrationGoogleDrive: gDriveToggled ? 1 : 0,
      IntegrationOutlook: outlookToggled ? 1 : 0,
      IntegrationOneDrive: oneDriveToggled ? 1 : 0,
      ConversationHistory: convHistoryToggled ? 1 : 0,
      ChatEmbeddings: chatMemoryToggled ? 1 : 0,
      IntegrationDesktop: desktopToggled ? 1 : 0,
    };
  
    try {
      // Update DB
      const resp = await fetch("/user-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        console.error("Error saving user settings:", resp.statusText);
        return;
      }
      console.log("User settings updated in DB");
  
      // For each toggle that was ON but is now OFF, call the relevant delete route
      if (originalStates.gmail && !gmailToggled) {
        await deleteSpecificEmbeddings("gmail");
      }
      if (originalStates.gdrive && !gDriveToggled) {
        await deleteSpecificEmbeddings("gdrive");
      }
      if (originalStates.outlook && !outlookToggled) {
        await deleteSpecificEmbeddings("outlook");
      }
      if (originalStates.onedrive && !oneDriveToggled) {
        await deleteSpecificEmbeddings("onedrive");
      }
      if (originalStates.convHistory && !convHistoryToggled) {
        await clearConversationHistory();
      }
      if (originalStates.chatMemory && !chatMemoryToggled) {
        await deleteSpecificEmbeddings("chat_history");
      }
      if (originalStates.desktop && !desktopToggled) {
        await deleteSpecificEmbeddings("desktop");
      }
  
      // Update originalStates to reflect the new toggles
      setOriginalStates({
        gmail: gmailToggled,
        gdrive: gDriveToggled,
        outlook: outlookToggled,
        onedrive: oneDriveToggled,
        convHistory: convHistoryToggled,
        chatMemory: chatMemoryToggled,
        desktop: desktopToggled,
      });
  
      // Only show alert if not showing activate button
      if (!showActivateButton) {
        alert("Settings saved!");
      }
      
    } catch (err) {
      console.error("Error saving user settings or deleting embeddings:", err);
    }
  };

  // ⭐ NEW: Handle activate button click
  const handleActivate = () => {
    if (setupToken) {
      console.log('[MyProfile] Opening deeplink with token');
      window.location.href = `desktopsync://setup?token=${setupToken}`;
      setDesktopSyncState('complete');
      localStorage.setItem('desktop-activated', 'true');

      // ⭐ Check if there are pending OAuth integrations
      const pendingOAuth = localStorage.getItem('pendingOAuthAfterDesktop');
      if (pendingOAuth) {
        const integrations = JSON.parse(pendingOAuth);
        localStorage.removeItem('pendingOAuthAfterDesktop');
        
        // Give the deeplink a moment to open, then redirect to OAuth
        setTimeout(() => {
          initiateOAuthFlow(integrations);
        }, 1000);
      }
    } else {
      alert('Setup token not found. Please try again.');
    }
  };

  // ⭐ NEW: Check on mount if already activated
  useEffect(() => {
    if (localStorage.getItem('desktop-activated') === 'true') {
      setShowActivateButton(false);
    }
  }, []);

  return (
    <div>
      <div
        className="my-profile-container"
        style={{
          background: isDark
            ? "rgba(25, 25, 30, 0.85)"
            : "rgba(255, 255, 255, 0.95)",
          color: isDark ? "white" : "#000",
        }}
      >
        <h1
          className="my-profile-title"
          style={{
            color: isDark ? "white" : "#000",
          }}
        >
          My Profile
        </h1>
        
        {/* --- ADDED EMAIL DISPLAY --- */}
        {primaryEmail && (
          <div className="primary-email-box">
            {primaryEmail}
          </div>
        )}
        
        <h2
          className="my-profile-subheading text-muted"
          style={{ fontWeight: "normal" }}
        >
          Connected services will personalize your AI conversations. Delete connections
          anytime.
        </h2>
        <div className="table">
          {/* COLUMN 1: Google Drive + OneDrive */}
          <div className="one">
            <div className="toggle-row">
              <span
                className="toggle-label"
                style={{
                  background: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={GoogleDriveIcon}
                    alt="Google Drive Icon"
                    className="app-icon"
                    style={{ height: "48px", width: "48px" }}
                  />
                  <div
                    className="app-title"
                    style={{ fontSize: "20px", marginLeft: "0.5rem" }}
                  >
                    Google Drive
                  </div>
                </div>
              </span>
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={gDriveToggled}
                  onChange={toggleGDrive}
                />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <span
                className="toggle-label"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div className="ms-BrandIcon--icon48 ms-BrandIcon--onedrive"></div>
                  <div className="text-container">
                    <span
                      className="app-title"
                      style={{ fontSize: "20px", marginLeft: "0.5rem" }}
                    >
                      OneDrive
                    </span>
                  </div>
                </div>
              </span>
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={oneDriveToggled}
                  onChange={toggleOneDrive}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          {/* COLUMN 2: Gmail + Outlook */}
          <div className="two">
            <div className="toggle-row">
              <div
                className="toggle-label"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={GmailIcon}
                    alt="Gmail Icon"
                    className="app-icon"
                    style={{ height: "48px", width: "48px" }}
                  />
                  <div
                    className="gmail-title"
                    style={{ marginLeft: "0.5rem", fontSize: "20px", display: "flex", alignItems: "center" }}
                  >
                    <div style={{ position: "relative", display: "inline-block", paddingRight: "18px" }}>
                      Gmail
                      <div className="info-icon-container" style={{ position: "absolute", top: "-2px", right: "0" }}>
                        <img src={InfoIcon} alt="Info" className="info-icon" />
                        <div className="info-tooltip">
                          Only conversations with a sent email from you
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> {/* <-- THIS WAS THE MISSING CLOSING TAG FOR GMAIL TOGGLE-LABEL */}
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={gmailToggled}
                  onChange={toggleGmail}
                />
                <span className="slider" />
              </label>
            </div>
  
            <div className="toggle-row">
              <div
                className="toggle-label"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "transparent",
                }}
              >
                <div className="ms-BrandIcon--icon48 ms-BrandIcon--outlook"></div>
                <div className="app-label">
                  <div className="text-container">
                    <div
                      className="app-title"
                      style={{ fontSize: "20px", marginLeft: "0.5rem", display: "flex", alignItems: "center" }}
                    >
                      <div style={{ position: "relative", display: "inline-block", paddingRight: "18px" }}>
                        Outlook
                        <div className="info-icon-container" style={{ position: "absolute", top: "-2px", right: "0" }}>
                          <img src={InfoIcon} alt="Info" className="info-icon" />
                          <div className="info-tooltip">
                            Only conversations with a sent email from you
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> {/* <-- THIS WAS THE MISSING CLOSING TAG FOR OUTLOOK TOGGLE-LABEL */}
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={outlookToggled}
                  onChange={toggleOutlook}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          {/* COLUMN 3: Desktop Files + Chat History */}
          <div className="three">
            <div className="toggle-row">
              <span
                className="toggle-label"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={DesktopFolderIcon}
                    alt="Desktop Folder Icon"
                    className="app-icon"
                    style={{ height: "48px", width: "48px" }}
                  />
                  <div
                    className="app-title"
                    style={{ fontSize: "20px", marginLeft: "0.5rem" }}
                  >
                    Desktop
                  </div>
                </div>
              </span>
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={desktopToggled}
                  onChange={toggleDesktop}
                />
                <span className="slider" />
              </label>
            </div>

            {/* Chat History toggle */}
            <div className="toggle-row">
              <span
                className="toggle-label"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={ChatHistoryIcon}
                    alt="Chat History Icon"
                    className="app-icon"
                    style={{ height: "54px", width: "54px" }}
                  />
                  <div
                    className="app-title"
                    style={{ fontSize: "20px", marginLeft: "0.5rem" }}
                  >
                    Chat History
                  </div>
                </div>
              </span>
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={chatMemoryToggled}
                  onChange={toggleChatMemory}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          {/* COLUMN 4: "All" toggle in its own row */}
          <div className="four">
            {/* Empty placeholder to take up left column space */}
            <div className="toggle-row" style={{ visibility: "hidden" }}></div>
            <div className="toggle-row">
              <span
                className="toggle-label"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <div
                  className="app-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* Invisible spacer to match icon width */}
                  <div style={{ height: "48px", width: "48px" }}></div>
                  <div
                    className="app-title"
                    style={{ fontSize: "20px", marginLeft: "0.5rem" }}
                  >
                    All
                  </div>
                </div>
              </span>
              <label
                className="switch"
                style={{
                  backgroundColor: "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={allToggled}
                  onChange={toggleAll}
                />
                <span className="slider" />
              </label>
            </div>
          </div>
        </div>
  
        {/* Button Container */}
        <div className="button-container">
          {desktopSyncState === 'idle' && (
            <button className="save-button" onClick={handleSave}>
              Save
            </button>
          )}
          
          {desktopSyncState === 'confirm_install' && (
            <div>
              <p style={{ 
                marginBottom: '1rem', 
                color: isDark ? '#17a2b8' : '#004085',
                backgroundColor: isDark ? 'rgba(23, 162, 184, 0.1)' : '#d1ecf1',
                padding: '0.75rem',
                borderRadius: '4px',
                border: isDark ? '1px solid rgba(23, 162, 184, 0.3)' : '1px solid #bee5eb',
                textAlign: 'center'
              }}>
                📥 <strong>Download Started!</strong> Please install the Desktop Sync (this takes about 10 seconds), then click below.
              </p>
              <button 
                className="save-button" 
                onClick={() => setDesktopSyncState('activate')}
              >
                I've Installed It
              </button>
            </div>
          )}
          
          {desktopSyncState === 'activate' && (
            <div>
              <p style={{ 
                marginBottom: '1rem', 
                color: isDark ? '#28a745' : '#155724',
                backgroundColor: isDark ? 'rgba(40, 167, 69, 0.1)' : '#d4edda',
                padding: '0.75rem',
                borderRadius: '4px',
                border: isDark ? '1px solid rgba(40, 167, 69, 0.3)' : '1px solid #c3e6cb',
                textAlign: 'center'
              }}>
                🚀 <strong>Ready to Activate!</strong> Click below to start syncing.
              </p>
              <button className="save-button activate-button" onClick={handleActivate}>
                Activate Desktop Sync
              </button>
            </div>
          )}
          
          {desktopSyncState === 'complete' && (
            <div>
              <p style={{ 
                marginBottom: '1rem', 
                color: isDark ? '#28a745' : '#155724',
                backgroundColor: isDark ? 'rgba(40, 167, 69, 0.1)' : '#d4edda',
                padding: '0.75rem',
                borderRadius: '4px',
                border: isDark ? '1px solid rgba(40, 167, 69, 0.3)' : '1px solid #c3e6cb',
                textAlign: 'center'
              }}>
                ✅ <strong>Desktop Sync Activated!</strong> Your Desktop files are now being added to your profile.
              </p>
              {/* NO BUTTON - Just show success message */}
            </div>
          )}
          
          <button 
            className="delete-button" 
            onClick={handleDeleteAccount}
            disabled={isDeleting}
          >
            {isDeleting 
              ? 'Deleting...' 
              : showDeleteConfirmation 
                ? 'Confirm' 
                : 'Delete Account'
            }
          </button>
        </div>
  
        {/* Delete Confirmation Message */}
        {showDeleteConfirmation && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            borderRadius: '4px',
            color: '#721c24'
          }}>
            <p><strong>Warning:</strong> Deleting your account will also delete all data from your profile.</p>
            <div style={{ marginTop: '0.5rem' }}>
              <button 
                onClick={cancelDelete}
                style={{
                  marginRight: '0.5rem',
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <span style={{ fontSize: '0.9rem', color: '#721c24' }}>
                Click "Confirm" again to proceed.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ⭐ NEW: Desktop Agent Install Modal for Linux users */}
      {showDesktopModal && (
        <DesktopAgentInstallModal onClose={() => setShowDesktopModal(false)} />
      )}
      
      <Footer />
    </div>
  );
}